/**
 * The shapes the menu templates take (`AppFrame`, `HomeTemplate`,
 * `ChallengesTemplate`, `TogetherTemplate`, `ListPage`) and the `Leaderboard`
 * part. Every word in them is the game's, already in its language.
 */
import type { Snippet } from 'svelte';

/** Where server-backed content stands. A template never fetches; the game says. */
export type ContentState = 'ready' | 'loading' | 'offline' | 'guest';

/** The round profile button `AppFrame` draws (the shell's `AvatarButton`). */
/**
 * The frame's offline notice (since 1.8.0): a quiet strip where the banner
 * goes, said politely (`role="status"`). Pass it whenever the game can be
 * offline, with `shown` following the connection: the status region then
 * stands in the frame from the start and only its words come and go, so
 * going offline is announced (a region inserted with its words already in
 * it often is not).
 */
export interface AppFrameOffline {
  /** Whether the player is offline now: the words are in the strip only then. */
  readonly shown: boolean;
  /** What it means, in the game's words ("You are offline"). */
  readonly message: string;
  /** A quieter second line ("Lessons you opened before still work."). */
  readonly detail?: string | undefined;
  /** On the status region; default `ui-app-frame-offline`. */
  readonly testid?: string | undefined;
}

export interface AppFrameAvatar {
  /** The player's initial, or `null` for a guest (a generic person). */
  readonly initial: string | null;
  /** The player's picture (TwinTen ADR 0073), drawn in place of the initial. */
  readonly src?: string | null | undefined;
  /** The button's accessible name ("Your profile and settings"). */
  readonly label: string;
  /** The name beside it in the desktop sidebar ("Sam", or "Guest"). */
  readonly name: string;
  /** `dialog` (default): it opens a sheet; `false`: it navigates. */
  readonly haspopup?: 'dialog' | false;
}

/**
 * The game's identity in the frame (`AppFrame`'s `game`, since 1.4.0): its
 * name and its mark, so a player always knows which game this is when every
 * game on the shell looks alike. The frame draws the mark as a rounded tile
 * filled with the accent, holding `mark`.
 */
export interface GameBrand {
  /**
   * The game's name as it is written ("Sample"). The Home heading's words
   * (unless `AppFrame`'s `brand` wordmark draws them) and the small mark's
   * accessible name on the other tabs. Not translated.
   */
  readonly name: string;
  /**
   * The glyph inside the tile: an inline SVG or a character sized in `em`
   * (the tile sets the size) and drawn in `currentColor` (the tile sets
   * `--ui-on-accent`). Absent: the name's first letter.
   */
  readonly mark?: Snippet | undefined;
}

/** Home's New game tile (the shell's `NewGameTile`). */
export interface HomeNewGame {
  readonly title: string;
  readonly detail?: string | undefined;
  /** The glyph in the round well; a plus by default. */
  readonly icon?: Snippet | undefined;
  readonly onclick: () => void;
  readonly testid?: string | undefined;
}

/**
 * One section of Home under its label ("Continue a game", "Today", "Your
 * bests"). `content` is the game's list; without it, `empty` is said instead.
 */
export interface HomeSection {
  /** Stable and unique within the page. */
  readonly id: string;
  readonly label: string;
  /** The list, stacked at the section's gap. Absent: the section is empty. */
  readonly content?: Snippet | undefined;
  /** What an empty section says will appear there. */
  readonly empty?: string | undefined;
  /** On the `<section>`. */
  readonly testid?: string | undefined;
  /** On the empty sentence. */
  readonly emptyTestid?: string | undefined;
}

/**
 * How many invites Home shows before "+N more" (`HomeInvites.limit`'s
 * default): enough for a busy evening, few enough that New game stays near
 * the top of a phone.
 */
export const HOME_INVITES_SHOWN = 3;

/** One answer on a Home invite: Join, or Decline. */
export interface HomeInviteAnswer {
  readonly label: string;
  readonly onclick: () => void;
  /** The button's accessible name, when its words need the who ("Join Robin's game"). */
  readonly name?: string | undefined;
  /** Join only: kept to size and said to be working; a second press is ignored. */
  readonly busy?: boolean | undefined;
  readonly testid?: string | undefined;
}

/** One invite on Home (the shell's `InviteCard`, compact): who asks, for what, two answers. */
export interface HomeInvite {
  /** Stable and unique (the invite's own id). */
  readonly id: string;
  /** The avatar's letter: the inviter's initial. */
  readonly initial: string;
  /** Who is asking ("Robin"). */
  readonly name: string;
  /** What for ("Co-op · Hard"). */
  readonly detail?: string | undefined;
  readonly join: HomeInviteAnswer;
  readonly decline: HomeInviteAnswer;
  /** On the card. */
  readonly testid?: string | undefined;
}

/**
 * Home's "Invites for you" (since 1.2.0): the invites to a game waiting for
 * this player, above New game. Drawn only when `items` has one.
 */
export interface HomeInvites {
  /** The section's label ("Invites for you"). */
  readonly label: string;
  /** Newest first; the game leaves out what it has no business showing (a guest, offline). */
  readonly items: readonly HomeInvite[];
  /** How many show before "+N more" reveals the rest ({@link HOME_INVITES_SHOWN}). */
  readonly limit?: number | undefined;
  /** The reveal's words for `count` hidden invites. Default: the shell's `invitesMore`. */
  readonly formatMore?: ((count: number) => string) | undefined;
  /** On the `<section>`. Default `ui-home-invites`. */
  readonly testid?: string | undefined;
  /** On "+N more". Default `ui-home-invites-more`. */
  readonly moreTestid?: string | undefined;
}

/** One way to start a game together (the shell's `StartTile`). */
export interface TogetherStart {
  readonly id: string;
  readonly title: string;
  readonly detail?: string | undefined;
  /** The glyph, 24px, in `currentColor`. */
  readonly icon: Snippet;
  /** `second` for the second of a pair, so two tiles side by side are told apart. */
  readonly tone?: 'accent' | 'second';
  readonly disabled?: boolean;
  readonly onclick: () => void;
  readonly testid?: string | undefined;
}

/** "Join a game" (the shell's `JoinTile`). */
export interface TogetherJoin {
  readonly title: string;
  readonly detail?: string | undefined;
  readonly icon?: Snippet | undefined;
  readonly disabled?: boolean;
  readonly onclick: () => void;
  readonly testid?: string | undefined;
}

/** The Together tab's list section: the games already going with somebody. */
export interface TogetherList {
  readonly label: string;
  /** The list. Absent: the section is empty and says `empty`. */
  readonly content?: Snippet | undefined;
  readonly empty?: string | undefined;
  /** On the label (it often carries a count a test reads). */
  readonly labelTestid?: string | undefined;
  readonly emptyTestid?: string | undefined;
}

/** What every `ListSection` has, whatever its rows are handed. */
export interface ListSectionFields {
  /** Stable and unique within the page; `searchAfter` names one. */
  readonly id: string;
  /** The small capitals above the group. Absent: no label. */
  readonly label?: string | undefined;
  /** A sentence under the group. */
  readonly note?: string | undefined;
  /** On the group around `content`. */
  readonly testid?: string | undefined;
}

/**
 * One section of a `ListPage`: a label, a group of rows, a note under it.
 *
 * `ListSection` (no type argument) is 1.3.0's: `content` is a snippet of no
 * argument. `ListSection<T>` (since 1.4.0) hands `content` the section's
 * `args`, so one snippet can draw several sections from their data
 * (`content: Snippet<[T]>`, `args: T`). A page mixes both freely.
 */
export type ListSection<T = never> = ListSectionFields &
  ([T] extends [never]
    ? {
        /** The rows. */
        readonly content: Snippet;
        readonly args?: undefined;
      }
    : {
        /** The rows, drawn from `args`. */
        readonly content: Snippet<[T]>;
        /** What `content` is handed. */
        readonly args: T;
      });

/**
 * Any section a `ListPage` takes: argument-less, or with `args` of any type
 * (each section's `content` is handed its own `args`, so the page need not
 * know their type).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- each section has its own T; the page only hands a section's args to that section's own snippet, so no single type fits them all.
export type AnyListSection = ListSection | ListSection<any>;

/**
 * A section written inline, typed (since 1.4.0): `T` is taken from
 * `content`'s parameter alone, so `args` is checked against it rather than
 * widening it (`sections={[listSection({ id, content: rows, args: data })]}`).
 * Without `args`, it is 1.3.0's argument-less section. It returns its
 * argument unchanged.
 */
export function listSection<T>(
  section: ListSectionFields & { readonly content: Snippet<[T]>; readonly args: NoInfer<T> },
): ListSection<T>;
export function listSection(
  section: ListSectionFields & { readonly content: Snippet; readonly args?: undefined },
): ListSection;
export function listSection(section: AnyListSection): AnyListSection {
  return section;
}

/**
 * The strict shape of a `ListPage` drawn as a full page (since 1.3.0): its
 * props stay as loose as 1.2.0's, so a game that wants the compiler to hold
 * it to a title and a way out intersects them with this:
 * `ComponentProps<typeof ListPage> & ListPagePageProps`.
 */
export interface ListPagePageProps {
  readonly frame: 'page';
  /** The page's title and accessible name. */
  readonly title: string;
  /** Done, Back, Escape and the Back gesture alike. */
  readonly onback: () => void;
}

/** One row of a `Leaderboard`. */
export interface LeaderboardRow {
  /** Stable and unique; `ownId` names one. */
  readonly id: string;
  readonly rank: number;
  readonly name: string;
  /** The score, formatted by the game ("12,400", "1:32.4"). */
  readonly score: string;
  /** A second line under the name ("40 lines · Hard"). */
  readonly detail?: string | undefined;
  /** This row's own test id, over the list's (`testids.row` / `testids.own`). */
  readonly testid?: string | undefined;
}
